import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { sleep } from 'utils';
import { menuEventData, MenuService } from '../menu.service';

@Component({
  selector: 'lib-menu-global',
  templateUrl: './menu-global.component.html',
  styleUrls: ['./menu-global.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuGlobalComponent {
  private menuService = inject(MenuService);
  private router = inject(Router);
  private _change = inject(ChangeDetectorRef);
  @ViewChild('template', { read: ViewContainerRef, static: false }) private template: ViewContainerRef;
  @ViewChild('menuContainer') private menuElement: ElementRef<HTMLDivElement>;
  public menuOpen = false;
  public menuClosing = false;
  public positioningElement: ElementRef<HTMLElement>;
  public menuPosition: [number, number] = [0, 0];
  public menuMinWidth = 95;
  public menuId?: string;
  public menuOnTop = false;

  @HostListener('window:resize', ['$event'])
  public winResize() {
    this.updatePosition();
  }

  @HostListener('document:scroll', ['$event'])
  public scroll() {
    this.updatePosition();
  }

  @HostListener('document:keydown', ['$event'])
  public keyDown(event: KeyboardEvent) {
    if (!this.menuOpen) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.arrowNavigate(1);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.arrowNavigate(-1);
    }
    if (event.key === 'Tab') {
      this.positioningElement?.nativeElement.focus();
      this.close();
    }
    if (event.key === 'Escape') {
      this.close();
    }
  }

  constructor() {
    this.menuService.menuEvents.subscribe(async event => {
      if (!event) this.onClose();
      else this.onOpen(event);
    });
    this.router.events.subscribe(event => {
      if (!(event instanceof NavigationStart)) return;
      this.close();
    });
  }

  private async onOpen(event: menuEventData) {
    if (this.menuOpen || this.menuClosing) this.template.clear();
    this.template.createEmbeddedView(event.template);
    this.positioningElement = event.positioningElement;
    this.menuId = event.id;
    await this.updatePosition();
    const parent: HTMLDivElement = this.template.element.nativeElement.parentElement;
    const links = parent.querySelectorAll('a');
    links.forEach(link => {
      link.setAttribute('role', 'menuitem');
      link.setAttribute('tabindex', '0');
      link.setAttribute('aria-disabled', 'false');
    });
    links[0]?.focus();
    this.menuOpen = true;
    this.menuClosing = false;
    this._change.markForCheck();
    await this.updatePosition();
  }

  public async onClose() {
    this.menuOpen = false;
    this.menuClosing = true;
    await sleep(200); // allow closing animation to finish before clearing the contents
    if (!this.menuClosing) return;
    this.menuClosing = false;
    this.template.clear();
    this.positioningElement = undefined;
    this._change.markForCheck();
  }

  public close() {
    this.menuService.close();
  }

  private arrowNavigate(change: number) {
    const parent: HTMLDivElement = this.template.element.nativeElement.parentElement;
    const links = Array.from(parent.querySelectorAll('a'));
    const link = <HTMLAnchorElement>parent.querySelector('a:focus');
    let index = links.indexOf(link);
    if (index === -1) index = 0;

    index += change;
    if (index < 0) index = links.length + index;
    if (index > links.length - 1) index -= links.length;

    links.forEach(l => l.classList.remove('focused'));
    links[index]?.classList.add('focused');
    links[index]?.focus();
  }

  public async updatePosition() {
    if (!this.positioningElement?.nativeElement) return;
    await sleep(0); // needed to give menuElement time to render (so that we know its height)
    const bounds = this.positioningElement?.nativeElement.getBoundingClientRect();
    this.menuPosition = this.calcPosition(bounds) || [0, 0];
    this.menuMinWidth = this.calcMinWidth(bounds) || 95;
    this._change.markForCheck();
  }

  public calcMinWidth(bounds: DOMRect): number {
    return bounds.right - bounds.left;
  }

  public calcPosition(bounds: DOMRect): [number, number] {
    let left = bounds.left;
    const menuBounds = this.menuElement.nativeElement.getBoundingClientRect();
    let top = bounds.top + bounds.height;
    this.menuOnTop = false;

    if (top + menuBounds.height > document.scrollingElement.scrollHeight - document.scrollingElement.scrollTop) {
      top -= menuBounds.height + bounds.height;
      this.menuOnTop = true;
    }
    const rightSpacing = 12;
    if (left + menuBounds.width > document.scrollingElement.scrollWidth - rightSpacing) {
      left -= menuBounds.width - bounds.width;
    }

    return [left, top];
  }
}
